// 1. React imports
import { useEffect, useState } from 'react';

// 2. External libraries
import { CircleCheck, ExternalLink, TriangleAlert } from 'lucide-react';

// 3. Internal components
import { TelegramConnectSteps } from '~/components/telegram/TelegramConnectSteps';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';
import { WizardActions } from '~/components/onboarding/WizardActions';

// 4. Type imports
import type { AnyWebSocketEvent } from '~/types/websocket';
import { isOnboardingConnectionVerifiedEvent } from '~/types/websocket';

// 5. Hooks and utilities
import { getTelegramBotName } from '~/config';
import { useWebSocketSubscription } from '~/hooks/useWebSocketSubscription';

const CONNECTION_TIMEOUT_MS = 45000; // Show the skip hint after 45s of waiting
const SUCCESS_DISPLAY_MS = 1500; // 1.5s success display

/**
 * Props for ConnectTelegramStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface ConnectTelegramStepProps {
  topicId: string;
  topicName: string;
  onConnectionCreated: (connectionId: string) => void;
  onBack: () => void;
  onSkip: () => void;
  showClientSuccess?: boolean;
}

/**
 * Connection state tracking for Telegram bot setup.
 *
 * - waiting: User has not connected yet, showing instructions
 * - success: Connection confirmed via WebSocket event
 */
type ConnectionState = 'waiting' | 'success';

/**
 * Step for connecting Telegram bot during onboarding wizard.
 *
 * Displays 4-step bot setup instructions and waits for
 * onboarding.connection_verified WebSocket event. Shows the skip hint after 45s of waiting.
 * Auto-advances after 1.5s success display.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.2.3: WebSocket event type guards for safe event handling.
 *
 * @example
 * ```tsx
 * <ConnectTelegramStep
 *   topicId="topic_123"
 *   topicName="My Agent Topic"
 *   onConnectionCreated={(connectionId) => setConnectionId(connectionId)}
 *   onBack={() => setStep(1)}
 *   onSkip={handleSkip}
 * />
 * ```
 */
export function ConnectTelegramStep({
  topicId,
  topicName,
  onConnectionCreated,
  onBack,
  onSkip,
  showClientSuccess = false,
}: ConnectTelegramStepProps) {
  const telegramBotName = getTelegramBotName();
  const [connectionState, setConnectionState] = useState<ConnectionState>('waiting');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);

  // Listen for WebSocket events using subscription (no new connection)
  useWebSocketSubscription((event: AnyWebSocketEvent) => {
    // Handle generic connection_verified event for Telegram connections
    if (
      isOnboardingConnectionVerifiedEvent(event) &&
      event.data.connectionType === 'telegram' &&
      event.data.topicId === topicId
    ) {
      setConnectionId(event.data.connectionId);
      setConnectionState('success');
    }
  });

  // Show the skip hint after CONNECTION_TIMEOUT_MS of waiting
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (connectionState === 'waiting') {
        setShowTimeoutMessage(true);
      }
    }, CONNECTION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [connectionState]);

  // Auto-advance after success display period
  useEffect(() => {
    if (connectionState === 'success' && connectionId) {
      const advanceTimeoutId = setTimeout(() => {
        onConnectionCreated(connectionId);
      }, SUCCESS_DISPLAY_MS);

      return () => clearTimeout(advanceTimeoutId);
    }
  }, [connectionState, connectionId, onConnectionCreated]);

  // Plain bot link (no start payload): the backend's /start handler does exact-match
  // parsing with no payload support, so a deep-linked topicId would be silently dropped.
  const telegramDeepLink = `https://t.me/${telegramBotName}`;

  return (
    <div>
      {/* Success Alert - Client Setup Success from Step 3 */}
      {showClientSuccess && (
        <Alert variant="success" className="mb-6">
          <CircleCheck />
          <AlertDescription>Client configured! Your MCP/REST connection is ready.</AlertDescription>
        </Alert>
      )}

      {/* Subtitle - Step title displayed by WizardProgress */}
      <p className="mb-6 text-sm text-muted-foreground">
        Follow the steps below to connect your Telegram account to {topicName}.
      </p>

      {/* Success State */}
      {connectionState === 'success' && (
        <Alert variant="success" className="mb-6">
          <CircleCheck />
          <AlertTitle>Connection successful!</AlertTitle>
          <AlertDescription>Proceeding to next step...</AlertDescription>
        </Alert>
      )}

      {/* Waiting State: Instructions */}
      {connectionState === 'waiting' && (
        <>
          <TelegramConnectSteps className="mb-6 text-base" />

          {/* Direct deep link - skips the search step for users on a device with Telegram installed */}
          <Button
            render={<a href={telegramDeepLink} target="_blank" rel="noopener noreferrer" />}
            nativeButton={false}
            variant="outline"
            className="mb-6"
          >
            Open @{telegramBotName} in Telegram
            <ExternalLink data-icon="inline-end" />
          </Button>

          {/* Waiting Indicator */}
          <div className="mb-6 flex items-center gap-4 rounded border border-border bg-card p-4">
            <Spinner className="size-6" data-testid="connect-telegram-waiting-spinner" />
            <p className="text-sm text-muted-foreground">Waiting for Telegram connection...</p>
          </div>

          {/* Timeout Message (shown after CONNECTION_TIMEOUT_MS of waiting) */}
          {showTimeoutMessage && (
            <Alert variant="warning" className="mb-6">
              <TriangleAlert />
              <AlertDescription>
                If this is taking too long, you can skip and try again later
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Wizard Actions */}
      <WizardActions
        onBack={onBack}
        onSkip={onSkip}
        showBack={connectionState === 'waiting'}
        isLoading={connectionState === 'success'}
      />
    </div>
  );
}
